import { useState, useEffect, useRef } from "react";
import AgentBoBApi from "../../../api/AgentBoBApi";

const useFetchAgentBoB = ({ userId, filters }) => {
	const [loading, setLoading] = useState(false);
	const [rows, setRows] = useState([]);
	const [total, setTotal] = useState(0);
	const [issuers, setIssuers] = useState([]);
	const [statuses, setStatuses] = useState([]);
	const [applicantCount, setApplicantCount] = useState(0);
	const [periods, setPeriods] = useState([]);
	const [period, setPeriod] = useState(null);
	const [periodRange, setPeriodRange] = useState({ start: null, end: null });

	const abortControllerRef = useRef(null);
	const debounceTimer = useRef(null);

	const fetchAgentBoB = async (overrideFilters = {}) => {
		const merged = { ...filters, ...overrideFilters };

		if (abortControllerRef.current) {
			abortControllerRef.current.abort();
		}
		abortControllerRef.current = new AbortController();
		const signal = abortControllerRef.current.signal;

		setLoading(true);

		try {
			const result = await AgentBoBApi.getAgentBoB({
				user_id: userId,
				page: Math.floor(merged.offset / merged.limit) + 1,
				limit: merged.limit,
				search: merged.search,
				policy_status: merged.policy_status,
				carrier: merged.carrier,
				effective_date_from: merged.effective_date_from,
				effective_date_to: merged.effective_date_to,
				period: merged.period,
				signal,
			});

			setRows(result.data);
			setTotal(result.total);
			setIssuers(result.issuers ?? []);
			setStatuses(result.statuses ?? []);
			setApplicantCount(result.applicant_total ?? 0);
			setPeriods(result.periods ?? []);
			setPeriod(result.period ?? null);
			setPeriodRange({ start: result.period_start ?? null, end: result.period_end ?? null });
		} catch (error) {
			if (error.name === "AbortError") return;
			console.error("Failed to fetch agent BoB:", error);
			setRows([]);
			setTotal(0);
		} finally {
			if (!signal.aborted) {
				setLoading(false);
			}
		}
	};

	useEffect(() => {
		if (debounceTimer.current) clearTimeout(debounceTimer.current);

		const isSearchChange = filters.search !== undefined;
		const delay = isSearchChange ? 300 : 0;

		debounceTimer.current = setTimeout(() => {
			fetchAgentBoB();
		}, delay);

		return () => clearTimeout(debounceTimer.current);
	}, [
		userId,
		filters.offset,
		filters.limit,
		filters.search,
		filters.policy_status,
		filters.carrier,
		filters.effective_date_from,
		filters.effective_date_to,
		filters.period,
	]);

	return {
		rows,
		total,
		loading,
		issuers,
		applicantCount,
		statuses,
		periods,
		period,
		periodRange,
		refetch: fetchAgentBoB,
	};
};

export default useFetchAgentBoB;
